import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Check, X, Edit3 } from 'lucide-react';
import { TaskItemProps } from '@/types';
import clsx from 'clsx';

export const TaskItem: React.FC<TaskItemProps> = ({
  task,
  isEditing,
  onToggle,
  onUpdate,
  onDelete,
  onStartEdit,
  onStopEdit
}) => {
  const [editContent, setEditContent] = useState(task.content);
  const inputRef = useRef<HTMLInputElement>(null);

  // 当开始编辑时，聚焦输入框并选中文本
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  // 重置编辑内容
  useEffect(() => {
    setEditContent(task.content);
  }, [task.content]);

  // 保存编辑
  const handleSave = () => {
    const trimmedContent = editContent.trim();
    if (trimmedContent && trimmedContent !== task.content) {
      onUpdate(task.id, trimmedContent);
    } else {
      onStopEdit();
    }
  };

  // 取消编辑
  const handleCancel = () => {
    setEditContent(task.content);
    onStopEdit();
  };

  // 处理键盘事件
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSave();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      handleCancel();
    }
  };

  // 处理双击编辑
  const handleDoubleClick = () => {
    if (!task.completed) {
      onStartEdit(task.id);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.8 }}
      transition={{ duration: 0.2 }}
      className={clsx(
        'flex items-center gap-2 p-2 rounded-lg transition-colors',
        'hover:bg-yellow-100/50',
        task.completed && 'opacity-60'
      )}
    >
      {/* 完成状态复选框 */}
      <motion.button
        whileTap={{ scale: 0.9 }}
        onClick={() => onToggle(task.id)}
        className={clsx(
          'flex-shrink-0 w-5 h-5 rounded border-2 transition-colors',
          'flex items-center justify-center',
          task.completed
            ? 'bg-green-500 border-green-500 text-white'
            : 'border-gray-400 hover:border-green-400'
        )}
      >
        {task.completed && (
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ duration: 0.2 }}
          >
            <Check size={12} />
          </motion.div>
        )}
      </motion.button>

      {/* 任务内容 */}
      <div className="flex-1 min-w-0">
        {isEditing ? (
          <input
            ref={inputRef}
            type="text"
            value={editContent}
            onChange={(e) => setEditContent(e.target.value)}
            onBlur={handleSave}
            onKeyDown={handleKeyDown}
            className={clsx(
              'w-full px-2 py-1 text-sm bg-white border border-gray-300',
              'rounded focus:outline-none focus:border-blue-400',
              'placeholder-gray-400'
            )}
            placeholder="输入任务内容..."
            maxLength={50}
          />
        ) : (
          <div
            onDoubleClick={handleDoubleClick}
            className={clsx(
              'text-sm cursor-pointer px-2 py-1 rounded',
              'hover:bg-white/50 transition-colors',
              task.completed && 'line-through text-gray-500',
              !task.completed && 'text-gray-800'
            )}
            title="双击编辑"
          >
            {task.content}
          </div>
        )}
      </div>

      {/* 操作按钮 */}
      <div className="flex items-center gap-1">
        {!isEditing && !task.completed && (
          <motion.button
            whileTap={{ scale: 0.9 }}
            onClick={() => onStartEdit(task.id)}
            className="flex-shrink-0 p-1 text-gray-400 hover:text-blue-500 transition-colors"
            title="编辑任务"
          >
            <Edit3 size={14} />
          </motion.button>
        )}
        
        <motion.button
          whileTap={{ scale: 0.9 }}
          onClick={() => onDelete(task.id)}
          className="flex-shrink-0 p-1 text-gray-400 hover:text-red-500 transition-colors"
          title="删除任务"
        >
          <X size={14} />
        </motion.button>
      </div>
    </motion.div>
  );
};